import invert from 'lodash/invert';
import pickBy from 'lodash/pickBy';
import { valueEmpty } from 'helpers';
import dayjs, { Dayjs } from 'dayjs';
import styled from 'styled-components';
import { weekDays } from '../boat/constants';

export const isEarlierThanEndLimit = (timeValue, endTime, lastValue) => {
  const timeValueIsEarlier = dayjs(timeValue, 'HH:mm').diff(dayjs(endTime, 'HH:mm')) < 0;
  const timeValueIsLaterThanLastValue =
    lastValue === undefined ? true : dayjs(lastValue, 'HH:mm').diff(dayjs(timeValue, 'HH:mm')) < 0;
  return timeValueIsEarlier && timeValueIsLaterThanLastValue;
};

export const isDurationAvailable = (
  date: string,
  boatCalendar: any,
  packageType: string,
  time: string | null = null,
  duration: number | null = null
) => {
  const dayData = boatCalendar[date];
  const limitName = packageType === 'captained' ? 'captained_durations' : 'bareboat_durations';

  if (!time || !duration) return true;

  // the whole day is not available
  if (dayData?.available === false) return false;

  const timeData = dayData?.updated_start_times?.[time];

  // if there isnt updated start time it's available
  if (!timeData) return true;

  // if time is unavailable duration is also not available
  if (timeData.available === false) return false;

  // we check if the duration is avaiable for that time and package type
  return timeData[limitName]?.includes(duration);
};

export const getBoatTimesOptions = (startDateValue: Dayjs, defaultStartTimes, boatStartTimes) => {
  const filteredDefaultValues = (defaultStartTimes || []).filter((d) => d.default);

  const dateValue = startDateValue || dayjs();
  const weekDay = dateValue.format('dddd');
  const weekDaysInvert = invert(weekDays);
  const weekDayNSelected = weekDaysInvert[weekDay];
  let availableTimes = (filteredDefaultValues || []).map((dv) => dv.value);

  // select default boat start times based on the selected day
  if (!valueEmpty(boatStartTimes)) {
    availableTimes = boatStartTimes[weekDayNSelected];
  }

  return availableTimes;
};

export const getTimeOptions = ({
  startDate,
  calendar,
  timeOptions,
  useDuration = false,
  bookingDuration,
  selectedBookingPackage,
}) => {
  let timeValue = '08:00';
  let lastValue;
  const endTime = '21:00';
  const timeStep = 60;

  let options = [];
  let unavailableTimes = [];

  const dateSelected = startDate || dayjs().format('YYYY-MM-DD');
  const calendarDate = calendar[dateSelected];

  if (calendarDate) {
    const selectedUnavailableTimes = pickBy(
      calendarDate.updated_start_times,
      (value) => !valueEmpty(value) && value.available === false
    );
    unavailableTimes = Object.keys(selectedUnavailableTimes);
  }

  const isAvailable = (timeOption, allAvailableTimes) => {
    const timeStatus = calendarDate.updated_start_times[timeOption];

    if (
      timeStatus?.available &&
      (timeStatus?.captained_durations || timeStatus?.bareboat_durations) &&
      useDuration &&
      bookingDuration?.value
    ) {
      const limitName =
        selectedBookingPackage?.package_type === 'captained' ? 'captained_durations' : 'bareboat_durations';
      return timeStatus[limitName]?.includes(parseInt(bookingDuration?.value, 10));
    }
    return allAvailableTimes.includes(timeOption);
  };

  if (!valueEmpty(timeOptions)) {
    const allAvailableTimes = timeOptions.filter((o) => !unavailableTimes.includes(o));

    options = timeOptions.map((o) => ({
      label: dayjs(o, 'HH:mm').format('h:mm A'),
      value: o,
      available: calendarDate ? isAvailable(o, allAvailableTimes) : allAvailableTimes.includes(o),
    }));
  } else {
    // if there are no boat start times, we use the default start times
    options.push({ label: dayjs(timeValue, 'HH:mm').format('h:mm A'), value: timeValue, available: true });
    while (isEarlierThanEndLimit(timeValue, endTime, lastValue)) {
      lastValue = timeValue;

      timeValue = dayjs(timeValue, 'HH:mm').add(timeStep, 'minutes').format('HH:mm');

      options.push({ label: dayjs(timeValue, 'HH:mm').format('h:mm A'), value: timeValue, available: true });
    }
  }

  if (!calendarDate?.available || calendarDate?.availablity === 'none') return [];

  return options;
};

export const TimeNotice = () => {
  const NoticeWrapper = styled.div`
    padding: 12px;
    border-radius: 8px;
    background: #f0f4f8;
    margin: 15px;
  `;
  const Text = styled.p`
    color: #17233c;
    font-size: 12px;
    text-align: center;
  `;

  return (
    <NoticeWrapper>
      <Text>Times shown reflect current availability.</Text>
    </NoticeWrapper>
  );
};

const ERROR_MESSAGES = {
  NO_AVAILABLE_TIMES: 'There are no available times for this day',
} as const;

// Utility functions
export const getErrorMessage = (options: any[], calendar: Record<string, any>, error: string | null): string | null => {
  if (options.length === 0 && Object.keys(calendar).length !== 0) {
    return ERROR_MESSAGES.NO_AVAILABLE_TIMES;
  }
  return error;
};

export const filterTimeOptions = (options: any[]) => options;
